import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  X,
  User,
  Phone,
  Calendar,
  CreditCard,
  FileText,
  Building,
  Download,
  MoreVertical,
  RefreshCw,
  ArrowLeftRight,
  PackageCheck,
  ClockCheck,
} from "lucide-react";
import {
  IEpcPayment,
  IRegisteredUserWithDetails,
} from "../../models/epcCustomer.model";

import UserDocsRender from "../../components/Document/UserDocsRender";
// import NetMeterFileCostTab from "./NetMeterFileCostTab";
import { motion } from "framer-motion";
import { Tab } from "../Tab/Tab";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import QuotationsList from "./QuotationsList";
import { getCustomerQuotes } from "../../store/slices/epcSlice";
import { IUser } from "../../models/userData.model";
import { generatePaymentPDF } from "./generatePaymentPDF";
import CoApplicantModal from "./CoApplicantModal";
import EpcOrdersTab from "./EpcOrdersTab";
import StatusHistoryTab from "./StatusHistory";

interface CustomerDetailsModalProps {
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  onClose: () => void;
  customer: IRegisteredUserWithDetails | null;
  actionItems: JSX.Element;
}

const CustomerDetailsModal: React.FC<CustomerDetailsModalProps> = ({
  isOpen,
  onClose,
  customer,
  setIsOpen,
  actionItems,
}) => {
  const currentUserRole = useSelector((state: RootState) => state.auth.role);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuCoords, setMenuCoords] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [showCoApplicantModal, setShowCoApplicantModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<IEpcPayment | null>(
    null,
  );
  const [coApplicantName, setCoApplicantName] = useState("");
  const [coApplicantMap, setCoApplicantMap] = useState<Record<string, string>>(
    {},
  );

  const [loadingPaymentId, setLoadingPaymentId] = useState<string | null>(null);

  const customerQuotation = useSelector(
    (state: RootState) => state.epc.customerQuotes,
  );

  const myAllPayments = useSelector(
    (state: RootState) => state.epc.currentEpcCustomer.allPayments,
  );

  const customerPayments =
    myAllPayments?.filter(
      (payment: IEpcPayment) =>
        payment.customerMobileNumber === customer?.mobileNumber,
    ) || [];

  const dispatch: RootDispatch = useDispatch();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  const [activeTab, setActiveTab] = useState<
    "overview" | "kyc" | "payments" | "quotations" | "orders" | "status"
  >("overview");

  useEffect(() => {
    if (activeTab === "quotations" && customer?.mobileNumber) {
      dispatch(getCustomerQuotes(customer.mobileNumber));
    }
  }, [activeTab, customer?.mobileNumber, dispatch]);

  // Close menu on outside click, ignore clicks on the menu button for the same payment
  useEffect(() => {
    if (!openMenuId) return;

    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current && menuRef.current.contains(target)) return;

      const el = (e.target as HTMLElement).closest(
        "[data-menu-button]",
      ) as HTMLElement | null;
      if (el && el.getAttribute("data-menu-button") === openMenuId) return;

      setOpenMenuId(null);
      setMenuCoords(null);
    };

    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
  }, [openMenuId]);

  if (!isOpen || !customer) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleDocumentClick = (e: React.MouseEvent, documentUrl: string) => {
    e.preventDefault();
    if (documentUrl) {
      window.open(documentUrl, "_blank");
    }
  };

  const handleDownloadPaymentReceipt = async (
    e: React.MouseEvent,
    paymentObj: IEpcPayment,
  ) => {
    e.preventDefault();
    setLoadingPaymentId(paymentObj._id);
    try {
      await generatePaymentPDF({
        customerName: paymentObj.customerName,
        paymentAmount: paymentObj.amount,
        paymentDate: new Date(paymentObj.paymentAt).toLocaleDateString("en-IN"),
        coApplicantName: coApplicantMap[paymentObj._id],
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setLoadingPaymentId(null);
      setOpenMenuId(null);
      setMenuCoords(null);
    }
  };

  const currentPayment = customer?.currentPayment;
  const roundOffValue = currentPayment?.roundOffAmount ?? 0;
  const adjustmentAmount = currentPayment?.quote?.adjustmentAmount ?? 0;

  const hasRoundOffValue =
    currentPayment?.roundOffAmount !== undefined &&
    currentPayment?.roundOffAmount !== 0;

  const hasAdjustment =
    adjustmentAmount !== undefined && adjustmentAmount !== 0;

  const paymentSummary = {
    totalBillAmount: currentPayment?.totalBillAmount ?? 0,
    paymentPaid: currentPayment?.paymentPaid ?? 0,
    paymentApproved: currentPayment?.paymentApproved ?? 0,
    remainingAmount: hasRoundOffValue
      ? (currentPayment?.totalBillAmount ?? 0) -
        (currentPayment?.paymentApproved ?? 0) -
        roundOffValue
      : hasAdjustment
        ? (currentPayment?.totalBillAmount ?? 0) -
          (currentPayment?.paymentApproved ?? 0) -
          adjustmentAmount
        : (currentPayment?.totalBillAmount ?? 0) -
          (currentPayment?.paymentApproved ?? 0),
    hasRoundOff: hasRoundOffValue,
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: User },
    { id: "kyc", label: "Documents", icon: FileText },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "quotations", label: "Quotations", icon: FileText },
    { id: "status", label: "Status", icon: ClockCheck },
    { id: "orders", label: "Orders", icon: PackageCheck },
    // ...(customer?.frelitHandleNetMeterFileCost && customer.quote?.totalCost
    //   ? [
    //       {
    //         id: "netMeterFileCost",
    //         label: "SR Payment",
    //         icon: File,
    //       },
    //     ]
    //   : []),
  ];

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 text-sm font-medium rounded";
    switch (status) {
      case "approved":
        return `${baseClasses} text-emerald-700 bg-emerald-100`;
      case "rejected":
        return `${baseClasses} text-red-700 bg-red-100`;
      default:
        return `${baseClasses} text-amber-700 bg-amber-100`;
    }
  };

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-2">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className=" relative w-full max-w-4xl bg-white shadow-2xl overflow-hidden h-screen sm:h-[90vh] rounded-none sm:rounded-lg ">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-3 sm:px-4 py-3 border-b border-gray-200">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-neutral-900 mb-1 break-words">
                {customer?.customerName}
              </h2>
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm text-neutral-500 break-all">
                  {customer?.mobileNumber}
                </span>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <Tab
            tabs={tabs}
            active={activeTab}
            setActiveTab={(id: string) =>
              setActiveTab(
                id as
                  | "overview"
                  | "kyc"
                  | "payments"
                  | "quotations"
                  | "orders"
                  | "status",
              )
            }
          />

          {/* Content */}
          <div className="p-3 sm:p-4 h-[calc(100vh-120px)] sm:h-[calc(90vh-120px)] overflow-y-auto">
            {activeTab === "overview" && (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 bg-blue-100 rounded-md flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Full Name</p>
                        <p className="text-sm font-medium text-gray-900">
                          {customer?.customerName}
                        </p>
                      </div>
                    </div>
                    {customer?.email && (
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 bg-blue-100 rounded-md flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="text-sm font-medium text-gray-900">
                            {customer?.email}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 bg-green-100 rounded-md flex items-center justify-center">
                        <Phone className="w-3.5 h-3.5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Mobile Number</p>
                        <p className="text-sm font-medium text-gray-900 font-mono">
                          {customer?.mobileNumber}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 bg-purple-100 rounded-md flex items-center justify-center">
                        <Building className="w-3.5 h-3.5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          Sales Representative
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {typeof customer?.salesRepresentative === "object"
                            ? customer?.salesRepresentative.name
                            : customer?.salesRepresentative ||
                              (customer?.cseID as IUser)?.username}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 bg-orange-100 rounded-md flex items-center justify-center">
                        <Calendar className="w-3.5 h-3.5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Created</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(customer?.createdAt || "")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Actions
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {actionItems}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "kyc" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                  <UserDocsRender
                    customer={customer}
                    handleCustomerClick={handleDocumentClick}
                    // documentTypes={customer?.kyc || []}
                    currentUserRole={currentUserRole!}
                  />
                </div>
              </div>
            )}

            {activeTab === "payments" && (
              <div className="space-y-4 h-full flex flex-col">
                {customer?.payments?.length! <= 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-neutral-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                        />
                      </svg>
                    </div>
                    <h3 className="text-base font-medium text-neutral-900 mb-2">
                      No Payments Found
                    </h3>
                    <p className="text-sm text-neutral-500">
                      No payments have been recorded yet.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg overflow-hidden flex flex-col flex-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {/* Total Bill */}
                      <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2">
                        <p className="text-xs font-medium text-blue-600">
                          Total Bill Amount
                        </p>
                        <p className="mt-1 text-lg font-bold text-blue-900">
                          ₹
                          {paymentSummary.totalBillAmount.toLocaleString(
                            "en-IN",
                          )}
                        </p>
                      </div>

                      {/* Paid */}
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2">
                        <p className="text-xs font-medium text-amber-600">
                          Current Payment
                        </p>
                        <p className="mt-1 text-lg font-bold text-amber-900">
                          ₹{paymentSummary.paymentPaid.toLocaleString("en-IN")}
                        </p>
                      </div>

                      {/* Approved */}
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2">
                        <p className="text-xs font-medium text-emerald-600">
                          Approved Amount
                        </p>
                        <p className="mt-1 text-lg font-bold text-emerald-900">
                          ₹
                          {paymentSummary.paymentApproved.toLocaleString(
                            "en-IN",
                          )}
                        </p>
                      </div>

                      {/* Remaining */}
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium text-red-600">
                            Remaining Amount
                          </p>
                        </div>
                        <p className="text-lg font-bold text-red-900">
                          ₹
                          {paymentSummary.remainingAmount.toLocaleString(
                            "en-IN",
                          )}
                        </p>
                      </div>
                    </div>
                    {!paymentSummary.hasRoundOff &&
                      !!currentPayment?.quote?.adjustmentAmount && (
                        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                          <div className="flex items-center justify-between">
                            {/* Left */}
                            <div className="flex items-start gap-2">
                              <ArrowLeftRight className="w-4 h-4 mt-0.5 text-gray-800" />

                              <div>
                                <div className="text-sm font-medium text-gray-800">
                                  Adjustment Amount
                                </div>
                                <div className="text-xs font-medium text-gray-800">
                                  Remark:{" "}
                                  <span>
                                    {currentPayment?.quote?.adjustmentRemark ||
                                      ""}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  Adjusted against total payment from dealer
                                  commission
                                </div>
                              </div>
                            </div>

                            {/* Right */}
                            <div className="text-right">
                              <div className="text-lg font-semibold text-gray-800">
                                ₹{" "}
                                {currentPayment?.quote?.adjustmentAmount?.toLocaleString(
                                  "en-IN",
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                    {paymentSummary.hasRoundOff && (
                      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-gray-800">
                            <RefreshCw className="w-4 h-4 text-gray-800" />

                            <span className="font-medium">
                              Round-off Amount
                            </span>
                          </div>

                          <div className="text-right">
                            <div className="text-lg font-semibold text-gray-800">
                              ₹{" "}
                              {currentPayment?.roundOffAmount?.toLocaleString(
                                "en-IN",
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="overflow-auto flex-1">
                      <div className="hidden md:block">
                        <table className="w-full">
                          <thead className="bg-neutral-50 border-b border-neutral-200">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                                Quote-Number
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                                Proof
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-neutral-200">
                            {customerPayments?.map(
                              (paymentObj: IEpcPayment, index: number) => (
                                <motion.tr
                                  key={paymentObj._id}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: index * 0.02 }}
                                  className="hover:bg-neutral-50 transition-colors"
                                >
                                  {/* Total payment amount */}
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-semibold text-neutral-900">
                                      ₹{paymentObj?.amount}{" "}
                                      <span className="text-gray-400">
                                        {paymentObj.isRoundOff === true
                                          ? "(Rounded Off)"
                                          : ""}
                                      </span>
                                    </div>
                                  </td>

                                  {/* Payment status */}
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                      className={getStatusBadge(
                                        paymentObj.status!,
                                      )}
                                    >
                                      {paymentObj.status}
                                    </span>
                                  </td>

                                  {/* Quotation Number */}
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-semibold text-neutral-900">
                                      #{paymentObj?.quotationNumber}
                                    </div>
                                  </td>

                                  {/* Payment proof */}
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center justify-between gap-2">
                                      {paymentObj.paymentProofUrl ? (
                                        <button
                                          onClick={(e) =>
                                            handleDocumentClick(
                                              e,
                                              paymentObj.paymentProofUrl!,
                                            )
                                          }
                                          className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                          View
                                        </button>
                                      ) : (
                                        <span className="text-xs text-neutral-400">
                                          No proof
                                        </span>
                                      )}

                                      <div className="relative">
                                        {currentUserRole !== "account" && (
                                          <button
                                            data-menu-button={paymentObj._id}
                                            onClick={(e) => {
                                              const btn =
                                                e.currentTarget as HTMLElement;
                                              const rect =
                                                btn.getBoundingClientRect();
                                              // Menu width (px). Using smaller width to avoid off-screen overflow.
                                              const menuWidth = 224; // 14rem
                                              const viewportLeft =
                                                window.scrollX + 8;
                                              const viewportRight =
                                                window.scrollX +
                                                window.innerWidth -
                                                8;
                                              let left =
                                                rect.right -
                                                menuWidth +
                                                window.scrollX;
                                              // Clamp horizontal position to viewport with 8px margin
                                              left = Math.min(
                                                Math.max(left, viewportLeft),
                                                viewportRight - menuWidth,
                                              );
                                              const top =
                                                rect.bottom +
                                                8 +
                                                window.scrollY;
                                              const isClosing =
                                                openMenuId === paymentObj._id;
                                              if (isClosing) {
                                                setOpenMenuId(null);
                                                setMenuCoords(null);
                                              } else {
                                                setMenuCoords({ top, left });
                                                setOpenMenuId(paymentObj._id);
                                              }
                                            }}
                                            className="p-2 rounded-md hover:bg-gray-100"
                                          >
                                            <MoreVertical className="w-4 h-4 text-gray-600" />
                                          </button>
                                        )}

                                        {openMenuId === paymentObj._id &&
                                          menuCoords &&
                                          createPortal(
                                            <div
                                              ref={menuRef}
                                              style={{
                                                position: "absolute",
                                                top: menuCoords.top,
                                                left: menuCoords.left,
                                                zIndex: 99999,
                                                width: `${224}px`,
                                              }}
                                              className="bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                                            >
                                              <div>
                                                <button
                                                  onClick={() => {
                                                    setSelectedPayment(
                                                      paymentObj,
                                                    );
                                                    setCoApplicantName(
                                                      coApplicantMap[
                                                        paymentObj._id
                                                      ] || "",
                                                    );
                                                    setShowCoApplicantModal(
                                                      true,
                                                    );
                                                    setOpenMenuId(null);
                                                    setMenuCoords(null);
                                                  }}
                                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap overflow-hidden"
                                                >
                                                  <User className="w-4 h-4 text-gray-500" />
                                                  <span className="truncate">
                                                    Add Co-Applicant Name
                                                  </span>
                                                </button>
                                                {paymentObj.status ===
                                                  "approved" && (
                                                  <button
                                                    onClick={(e) =>
                                                      handleDownloadPaymentReceipt(
                                                        e,
                                                        paymentObj,
                                                      )
                                                    }
                                                    disabled={
                                                      loadingPaymentId ===
                                                      paymentObj._id
                                                    }
                                                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                                                      loadingPaymentId ===
                                                      paymentObj._id
                                                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                                        : "text-gray-700 hover:bg-gray-50"
                                                    }`}
                                                  >
                                                    {loadingPaymentId ===
                                                    paymentObj._id ? (
                                                      <>
                                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-600" />
                                                        <span className="truncate">
                                                          Generating...
                                                        </span>
                                                      </>
                                                    ) : (
                                                      <>
                                                        <Download className="w-4 h-4 text-gray-500" />
                                                        <span className="truncate">
                                                          Download Receipt
                                                        </span>
                                                      </>
                                                    )}
                                                  </button>
                                                )}
                                              </div>
                                            </div>,
                                            document.body,
                                          )}
                                      </div>
                                    </div>
                                  </td>
                                </motion.tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="md:hidden space-y-3">
                        {customerPayments?.map((paymentObj) => (
                          <div
                            key={paymentObj._id}
                            className="border rounded-lg p-3 bg-white"
                          >
                            <div className="flex justify-between items-center">
                              <div className="font-semibold">
                                ₹{paymentObj.amount}{" "}
                                <span className="text-gray-400">
                                  {paymentObj.isRoundOff === true
                                    ? "(Rounded Off)"
                                    : ""}
                                </span>
                              </div>
                              <div className="text-sm font-semibold text-neutral-900">
                                #{paymentObj?.quotationNumber}
                              </div>
                              <span
                                className={getStatusBadge(paymentObj.status!)}
                              >
                                {paymentObj.status}
                              </span>
                            </div>

                            <div className="mt-3 flex gap-2">
                              {paymentObj.paymentProofUrl && (
                                <button
                                  onClick={(e) =>
                                    handleDocumentClick(
                                      e,
                                      paymentObj.paymentProofUrl!,
                                    )
                                  }
                                  className="px-3 py-2 bg-blue-600 text-white rounded"
                                >
                                  View Proof
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* New Net Meter File Cost Tab */}
            {/* {activeTab === "netMeterFileCost" && (
              <NetMeterFileCostTab
                customer={customer}
                isPaymentDoneOnSalesRep={customer?.isPaymentDoneOnSalesRep!}
                customerId={customer?._id!}
              />
            )} */}
            {activeTab === "quotations" && (
              <QuotationsList
                setActiveTab={setActiveTab}
                quotations={customerQuotation}
                isMainModalOpen={isOpen!}
                setIsMainModalOpen={setIsOpen}
              />
            )}
            {activeTab === "orders" && (
              <div className="mt-2">
                <EpcOrdersTab />
              </div>
            )}
            {activeTab === "status" && (
              <div className="mt-2">
                <StatusHistoryTab custId={customer?._id!} />
              </div>
            )}
            <CoApplicantModal
              isOpen={showCoApplicantModal && !!selectedPayment}
              value={coApplicantName}
              onChange={setCoApplicantName}
              onCancel={() => setShowCoApplicantModal(false)}
              onConfirm={() => {
                if (!selectedPayment) return;

                setCoApplicantMap((prev) => ({
                  ...prev,
                  [selectedPayment._id]: coApplicantName,
                }));

                setShowCoApplicantModal(false);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsModal;
